import type React from "react";
import { useEffect, useState } from "react";
import { insights } from "@duress/shared";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUpdateClientIndustries } from "./useClients";

interface Props {
  clientId: string;
  clientName: string;
  currentIndustries: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IndustriesDialog({
  clientId,
  clientName,
  currentIndustries,
  open,
  onOpenChange,
}: Props): React.JSX.Element {
  const save = useUpdateClientIndustries(clientId);
  const [selected, setSelected] = useState<string[]>(currentIndustries);

  // Re-sync local state when the dialog re-opens or the saved value changes.
  useEffect(() => {
    if (open) setSelected(currentIndustries);
  }, [open, currentIndustries]);

  const toggle = (industry: string): void => {
    setSelected((prev) =>
      prev.includes(industry) ? prev.filter((i) => i !== industry) : [...prev, industry],
    );
  };

  const onSave = async (): Promise<void> => {
    await save.mutateAsync(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Industries — {clientName}</DialogTitle>
        <DialogDescription>
          Pick one or more industries. These tags drive the Industry Insights peer
          benchmarks for this client.
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-2">
        {insights.INDUSTRIES.map((industry) => {
          const checked = selected.includes(industry);
          return (
            <label
              key={industry}
              className="flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm transition-colors hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(industry)}
                className="h-4 w-4 rounded border-input"
              />
              <span>{industry}</span>
            </label>
          );
        })}
      </div>

      {save.error ? (
        <p className="mt-3 text-sm text-destructive">Couldn't save industries. Try again.</p>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={save.isPending}
        >
          Cancel
        </Button>
        <Button type="button" onClick={() => void onSave()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
